/*
  SMA — Product page enhancements
  ---------------------------------------------------------------------------
  1. <sma-sticky-atc>  mobile sticky Add to cart bar, with three visibility
     modes driven by Theme settings > SMA Product Page.
  2. Price inside the Add to cart button, kept in sync with the selected
     variant.

  Deliberately self-contained: it watches the DOM rather than hooking Dawn's
  pub/sub, so it keeps working if product-info.js is updated.
*/
(function () {
  'use strict';

  /* ----------------------------------------------------------------------
     1. Sticky Add to cart bar
     ---------------------------------------------------------------------- */
  if (!customElements.get('sma-sticky-atc')) {
    customElements.define(
      'sma-sticky-atc',
      class SmaStickyAtc extends HTMLElement {
        connectedCallback() {
          this.mode = this.dataset.visibility || 'past_atc';
          this.sectionId = this.dataset.section;

          // Always mode needs no scroll tracking at all.
          if (this.mode === 'always') {
            this.toggle(true);
            return;
          }

          this.anchor =
            document.querySelector('#ProductInfo-' + this.sectionId + ' .product-form__buttons') ||
            document.getElementById('ProductSubmitButton-' + this.sectionId);

          // No anchor to track (or no IO support) — fall back to always on.
          if (!this.anchor || !('IntersectionObserver' in window)) {
            this.toggle(true);
            return;
          }

          this.observer = new IntersectionObserver(
            (entries) => {
              const entry = entries[entries.length - 1];
              if (this.mode === 'atc_hidden') {
                // Visible whenever the real button is off-screen, above or below.
                this.toggle(!entry.isIntersecting);
              } else {
                // past_atc — only once the real button has scrolled off the top.
                this.toggle(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
              }
            },
            { threshold: 0 }
          );

          this.observer.observe(this.anchor);
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }

        toggle(visible) {
          this.classList.toggle('is-visible', !!visible);
        }
      }
    );
  }

  /* ----------------------------------------------------------------------
     2. Price in the Add to cart button + sticky bar state sync
     ---------------------------------------------------------------------- */

  // Reads the price currently shown in the PDP price block, honouring sale state.
  function currentPrice(sectionId) {
    const root = document.getElementById('price-' + sectionId);
    if (!root) return null;

    const wrapper = root.querySelector('.price');
    if (!wrapper || wrapper.classList.contains('price--sold-out')) return null;

    const node = wrapper.classList.contains('price--on-sale')
      ? root.querySelector('.price__sale .price-item--sale')
      : root.querySelector('.price__regular .price-item--regular');

    if (!node) return null;
    const text = node.textContent.trim();
    return text.length ? text : null;
  }

  function syncSection(sectionId, state) {
    if (state.busy) {
      // Don't drop this batch — replay it once the current pass settles.
      state.pending = true;
      return;
    }
    state.busy = true;
    state.pending = false;

    const price = currentPrice(sectionId);
    const mainButton = document.getElementById('ProductSubmitButton-' + sectionId);
    const mainLabel = mainButton ? mainButton.querySelector('span') : null;
    const disabled = mainButton ? mainButton.hasAttribute('disabled') : false;

    // Price chips (main button and/or sticky bar).
    document
      .querySelectorAll('[data-sma-atc-price][data-section="' + sectionId + '"]')
      .forEach(function (chip) {
        if (price && !disabled) {
          chip.textContent = price;
          chip.hidden = false;
        } else {
          chip.hidden = true;
        }
      });

    // Mirror the real button's label + disabled state onto the sticky bar,
    // and keep the sticky Buy Now form pointed at the selected variant.
    const realId = document.querySelector('#product-form-' + sectionId + ' input[name="id"]');

    document
      .querySelectorAll('.sma-sticky-atc[data-section="' + sectionId + '"]')
      .forEach(function (bar) {
        const button = bar.querySelector('.sma-sticky-atc__button');
        if (button) {
          if (disabled) button.setAttribute('disabled', 'disabled');
          else button.removeAttribute('disabled');
        }

        const label = bar.querySelector('.sma-sticky-atc__label');
        if (label && mainLabel) label.textContent = mainLabel.textContent.trim();

        const stickyId = bar.querySelector('input[name="id"]');
        if (stickyId && realId) stickyId.value = realId.value;
      });

    // Re-bind gallery dots in case the media gallery was re-rendered.
    document.querySelectorAll('[data-sma-gallery-dots]').forEach(initDots);

    // Let the observers settle before we listen again.
    window.requestAnimationFrame(function () {
      state.busy = false;
      if (state.pending) syncSection(sectionId, state);
    });
  }

  function initSection(sectionId) {
    const state = { busy: false, pending: false };
    const sync = function () {
      syncSection(sectionId, state);
    };

    const priceRoot = document.getElementById('price-' + sectionId);
    if (priceRoot) {
      new MutationObserver(sync).observe(priceRoot, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    const mainButton = document.getElementById('ProductSubmitButton-' + sectionId);
    if (mainButton) {
      new MutationObserver(sync).observe(mainButton, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    const realId = document.querySelector('#product-form-' + sectionId + ' input[name="id"]');
    if (realId) realId.addEventListener('change', sync);

    sync();
  }

  /* ----------------------------------------------------------------------
     3. Mobile gallery pagination dots
     ---------------------------------------------------------------------- */

  function initDots(dots) {
    if (dots.dataset.smaDotsReady === 'true') return;
    dots.dataset.smaDotsReady = 'true';

    const sliderComponent = dots.closest('slider-component');
    if (!sliderComponent) return;

    const buttons = Array.prototype.slice.call(dots.querySelectorAll('.sma-gallery-dot'));
    if (!buttons.length) return;

    // The dot count comes from Liquid's media_count, which can disagree with
    // the number of slides actually rendered (hidden variant images, a limit).
    // Reconcile in both directions so every slide has exactly one dot.
    const slideCount = sliderComponent.querySelectorAll('.slider__slide').length;
    if (slideCount) {
      while (buttons.length > slideCount) {
        buttons.pop().remove();
      }
      while (buttons.length < slideCount) {
        const extra = buttons[0].cloneNode(false);
        extra.classList.remove('is-active');
        extra.removeAttribute('aria-current');
        extra.dataset.index = String(buttons.length);
        extra.setAttribute('aria-label', String(buttons.length + 1));
        dots.appendChild(extra);
        buttons.push(extra);
      }
    }

    const setActive = function (index) {
      buttons.forEach(function (dot, i) {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    // Dawn's SliderComponent fires this whenever the visible slide changes,
    // covering swipe, arrows and programmatic moves alike.
    sliderComponent.addEventListener('slideChanged', function (event) {
      const page = event.detail && event.detail.currentPage;
      if (typeof page === 'number') setActive(page - 1);
    });

    // Clicking a dot scrolls the slider to that slide.
    dots.addEventListener('click', function (event) {
      const dot = event.target.closest('.sma-gallery-dot');
      if (!dot) return;

      const index = parseInt(dot.dataset.index, 10);
      const slider = sliderComponent.querySelector('.slider');
      if (!slider || isNaN(index)) return;

      const slides = slider.querySelectorAll('.slider__slide');
      if (!slides[index] || !slides[0]) return;

      slider.scrollTo({
        left: slides[index].offsetLeft - slides[0].offsetLeft,
        behavior: 'smooth',
      });
      setActive(index);
    });
  }

  function init() {
    document.querySelectorAll('[data-sma-product-section]').forEach(function (node) {
      // The marker element is replaced with the section, so its own flag is a
      // reliable "already wired" test. Without it, a theme-editor section load
      // would stack a second set of observers on the surviving price block and
      // submit button, and every variant change would run sync twice over.
      if (node.dataset.smaReady === 'true') return;
      node.dataset.smaReady = 'true';
      const id = node.getAttribute('data-sma-product-section');
      if (id) initSection(id);
    });

    document.querySelectorAll('[data-sma-gallery-dots]').forEach(initDots);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when the theme editor swaps the product section.
  document.addEventListener('shopify:section:load', init);
})();